import type { HeroLevelResponse } from '@kidshome/api-contract';
import { HEROES } from '@kidshome/hero-content';

/**
 * The kid-facing hero LEVEL (ADR-0103) — the per-hero ✨-earned standing resolved by the SERVER
 * (`HeroResponse.level`: value/max/labelKey/starsEarned/starsIntoLevel/starsForNextLevel/starsToNext),
 * enriched here with a `fraction` (ring fill toward the next level) + `atMax`. This replaces the retired
 * client-side bond-level derivation: the level is now per-hero ✨ vs the hero's authored level curve, so the
 * web never re-derives it — it reads one number from the API. Forms are LABELS on this ladder (`labelKey`,
 * null on a plain "Level N" rung); at the top the bar is full and ✨ flows on to the wallet (never a dead-end).
 */
export interface HeroLevel extends HeroLevelResponse {
  /** Ring fill toward the next level, 0..1 (1 at the top of the ladder). */
  readonly fraction: number;
  /** At the top of the finite ladder — there is no next level (the "next" fields are null). */
  readonly atMax: boolean;
}

/** Enrich the server level standing with the derived ring `fraction` + `atMax` flag. Pure + total. */
export function heroLevel(level: HeroLevelResponse | null | undefined): HeroLevel {
  // A DEGRADED hero response (the payload carries its own `degraded` flag) can omit the level projection.
  // `useHero` maps `heroLevel(data.level)` and runs on EVERY kid route (HeroThemeApplier lives in the shell),
  // so a missing level must never hard-crash the whole kid experience into an error page — un-kind for a child
  // (ADR-0029). Degrade to a calm "Level 1, no next milestone" standing (full ring, no dead-end) instead.
  if (!level) {
    return {
      value: 1,
      max: 1,
      labelKey: null,
      starsEarned: 0,
      starsIntoLevel: 0,
      starsForNextLevel: null,
      starsToNext: null,
      fraction: 1,
      atMax: true,
    };
  }
  const span = level.starsForNextLevel;
  const atMax = span === null || level.starsToNext === null;
  const fraction =
    atMax || span === null || span <= 0 ? 1 : Math.max(0, Math.min(1, level.starsIntoLevel / span));
  return { ...level, fraction, atMax };
}

/**
 * The FIGURE form to render for a hero at a per-hero ✨ LEVEL (ADR-0103). Forms are milestone LABELS on the
 * per-hero level ladder (`levels[i].labelKey` marks a form), so the rendered form is the count of LABELLED
 * rungs at or before the current level, minus one (clamped into the form range). This ties the figure to the
 * hero's OWN ✨ level — a fresh hero (level 1) shows its FIRST form, never jumping ahead because the kid's
 * SHARED bond is high (the flaw in the legacy `stageIndex`, which ran a bond FSM over the kid-global bond).
 * Reads the authored ladder from the compiled catalog; an unknown archetype degrades to form 0.
 */
export function formIndexForLevel(
  archetypeId: string,
  levelValue: number,
  stageCount: number,
): number {
  const levels = HEROES.find((h) => h.archetypeId === archetypeId)?.levels ?? [];
  const labelled = levels
    .slice(0, Math.max(0, levelValue))
    .filter((r) => r.labelKey !== undefined).length;
  return Math.max(0, Math.min(Math.max(0, stageCount - 1), labelled - 1));
}
